import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { ProfileLayout } from '../components/Profile';
import ProfileSection from '../components/Profile/sections/ProfileSection';
import type { ActionSheetItem } from '../components/SpecActionSheet/SpecActionSheet';
import { UserTag } from '../components/UserTag/UserTag';
import type { UserType } from '../components/UserTag/UserTag';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import { DefaultAvatar, ChevronRightIcon } from '../components/icons';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import { useShare } from '../hooks/useShare';
import { useBands, useMyMusician } from '../api/queries';
import './UserProfile.css';

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { data: allBands, isLoading: bandsLoading } = useBands();
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastKeyRef = useRef(0);
  const showToast = useCallback((msg: string) => { toastKeyRef.current += 1; setToastMessage(msg); }, []);
  const share = useShare(showToast);

  // Fetch the user's linked musician profile (try for any logged in user)
  const { data: myMusician } = useMyMusician(!!user);

  // Get user roles from claims
  const userRoles = user?.claims?.roles || [];
  // Consider user a musician if they have the role OR have a linked musician profile
  const isMusician = userRoles.includes('musician') || !!myMusician;
  const isFan = (userRoles.includes('fan') || userRoles.length === 0) && !isMusician;
  const isAdmin = userRoles.includes('admin');

  // Get band roles from claims
  const bandRoles = user?.claims?.bandRoles || {};
  const bandIds = Object.keys(bandRoles);

  // Map band IDs to band data
  const userBands = bandIds
    .map(bandId => {
      const band = allBands?.find(b => b.id === bandId);
      const roleInfo = bandRoles[bandId];
      const roleName = roleInfo?.role || 'member';
      if (!band) return null;
      return {
        name: band.name,
        avatarUrl: band.photo_url,
        route: `/bands/${band.id}`,
        subtitle: roleName === 'band-admin' ? 'Admin' : roleName === 'band-show-contributor' ? 'Contributor' : 'Member',
      };
    })
    .filter(b => b !== null);

  const handleSignOut = async () => {
    await signOut();
    navigate('/home');
  };

  const actionItems: ActionSheetItem[] = [
    { key: 'share', label: 'Share Profile', onClick: () => share({ title: user?.displayName || 'My Profile' }) },
    { key: 'copy-link', label: 'Copy Link', onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); showToast('Link Copied'); } },
  ];

  // Not logged in state
  if (!user) {
    return (
      <ProfileLayout
        profileType="fan"
        heroVariant="none"
        actionSheetItems={[]}
      >
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">Welcome to Spec</h2>
          <p className="user-profile__empty-subtitle">Sign in to access your profile, manage your bands, and connect with the music community.</p>
          <div className="user-profile__auth-buttons">
            <Button
              type="primary"
              size="large"
              shape="round"
              block
              onClick={() => navigate('/signin')}
            >
              Sign In
            </Button>
            <Button
              size="large"
              shape="round"
              block
              onClick={() => navigate('/signup')}
              className="user-profile__signup-btn"
            >
              Create Account
            </Button>
          </div>
        </div>
      </ProfileLayout>
    );
  }

  return (
    <ProfileLayout
      profileType="fan"
      heroVariant="none"
      actionSheetItems={actionItems}
    >
      {/* User Identity Section */}
      <div className="band-identity">
        <div className="band-identity__avatar">
          <div className="band-identity__avatar-placeholder band-identity__avatar-placeholder--circle">
            <DefaultAvatar size={48} />
          </div>
        </div>
        <div className="band-identity__info">
          <h1 className="band-identity__name">{user.displayName || 'User'}</h1>
          <p className="user-profile__email">{user.email}</p>
          <div className="band-identity__badges">
            {/* Only show fan tag if not a musician (Bug 47) */}
            {isFan && !isMusician && <UserTag type="fan" onClick={() => setOpenInfoType('fan')} />}
            {isMusician && <UserTag type="musician" onClick={() => setOpenInfoType('musician')} />}
            {bandIds.some(id => bandRoles[id]?.role === 'band-show-contributor') && (
              <UserTag type="showContributor" onClick={() => setOpenInfoType('showContributor')} />
            )}
          </div>
        </div>
        <UserTypeInfo type="fan" open={openInfoType === 'fan'} onClose={() => setOpenInfoType(null)} />
        <UserTypeInfo type="musician" open={openInfoType === 'musician'} onClose={() => setOpenInfoType(null)} />
        <UserTypeInfo type="showContributor" open={openInfoType === 'showContributor'} onClose={() => setOpenInfoType(null)} />
      </div>

      {/* Quick links to musician profile and bands */}
      {(myMusician || userBands.length > 0) && (
        <div className="user-profile__profile-links">
          {myMusician && (
            <button
              className="user-profile__profile-link"
              onClick={() => navigate(`/musicians/${myMusician.id}`)}
            >
              <div className="user-profile__profile-link-content">
                {myMusician.photo_url ? (
                  <img src={myMusician.photo_url} alt="" className="user-profile__profile-link-avatar" />
                ) : (
                  <div className="user-profile__profile-link-avatar user-profile__profile-link-avatar--placeholder">
                    <DefaultAvatar size={32} />
                  </div>
                )}
                <div className="user-profile__profile-link-text">
                  <span className="user-profile__profile-link-name">{myMusician.first_name} {myMusician.last_name}</span>
                  <span className="user-profile__profile-link-label">Musician Profile</span>
                </div>
              </div>
              <ChevronRightIcon size="m" tone="darkGrey" />
            </button>
          )}
          {userBands.map((band) => band && (
            <button
              key={band.route}
              className="user-profile__profile-link"
              onClick={() => navigate(band.route)}
            >
              <div className="user-profile__profile-link-content">
                {band.avatarUrl ? (
                  <img src={band.avatarUrl} alt="" className="user-profile__profile-link-avatar user-profile__profile-link-avatar--rounded" />
                ) : (
                  <div className="user-profile__profile-link-avatar user-profile__profile-link-avatar--rounded user-profile__profile-link-avatar--placeholder">
                    <DefaultAvatar size={32} />
                  </div>
                )}
                <div className="user-profile__profile-link-text">
                  <span className="user-profile__profile-link-name">{band.name}</span>
                  <span className="user-profile__profile-link-label">{band.subtitle}</span>
                </div>
              </div>
              <ChevronRightIcon size="m" tone="darkGrey" />
            </button>
          ))}
        </div>
      )}

      {/* Quick Actions Section */}
      <ProfileSection title="Quick Actions">
        <div className="user-profile__actions-list">
          {isMusician && (
            <button className="user-profile__action-item" onClick={() => navigate('/add-band')}>
              <span className="user-profile__action-label">Create a Band</span>
              <ChevronRightIcon size="m" tone="darkGrey" />
            </button>
          )}
          <button className="user-profile__action-item" onClick={() => navigate('/add-show')}>
            <span className="user-profile__action-label">Add a Show</span>
            <ChevronRightIcon size="m" tone="darkGrey" />
          </button>
          {isMusician && (
            <button className="user-profile__action-item" onClick={() => navigate('/edit-musician-profile')}>
              <span className="user-profile__action-label">Edit Musician Profile</span>
              <ChevronRightIcon size="m" tone="darkGrey" />
            </button>
          )}
          {isFan && !isMusician && (
            <button className="user-profile__action-item" onClick={() => navigate('/edit-fan-profile')}>
              <span className="user-profile__action-label">Edit Fan Profile</span>
              <ChevronRightIcon size="m" tone="darkGrey" />
            </button>
          )}
        </div>
      </ProfileSection>

      {/* Settings Section */}
      <ProfileSection title="Settings">
        <div className="user-profile__actions-list">
          {/* Link to public fan profile (Bug 46) */}
          <button className="user-profile__action-item" onClick={() => navigate(`/fans/${user.uid}`)}>
            <span className="user-profile__action-label">View My Public Profile</span>
            <ChevronRightIcon size="m" tone="darkGrey" />
          </button>
          <button className="user-profile__action-item" onClick={() => navigate('/about')}>
            <span className="user-profile__action-label">About Spec</span>
            <ChevronRightIcon size="m" tone="darkGrey" />
          </button>
          <button className="user-profile__action-item" onClick={() => navigate('/contact')}>
            <span className="user-profile__action-label">Contact Us</span>
            <ChevronRightIcon size="m" tone="darkGrey" />
          </button>
          {isAdmin && (
            <button className="user-profile__action-item" onClick={() => navigate('/admin/users')}>
              <span className="user-profile__action-label">User Management</span>
              <ChevronRightIcon size="m" tone="darkGrey" />
            </button>
          )}
        </div>
      </ProfileSection>

      {/* Sign Out Section */}
      <ProfileSection title="Account">
        <div className="user-profile__signout-section">
          <Button
            size="large"
            shape="round"
            block
            onClick={handleSignOut}
            className="user-profile__signout-btn"
          >
            Sign Out
          </Button>
        </div>
      </ProfileSection>

      <SpecAlert key={toastKeyRef.current} message={toastMessage} onDone={() => setToastMessage(null)} />
    </ProfileLayout>
  );
};

export default UserProfile;
